import type {
  DatasetCsvForeignKey,
  DatasetCsvForeignKeyReference,
  DatasetCsvReference,
} from '@/types/source-types';

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item): item is string => Boolean(item));
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? [trimmed] : [];
  }

  return [];
};

const normalizeForeignKey = (
  foreignKey: DatasetCsvForeignKey,
): DatasetCsvForeignKey | null => {
  const columns = toStringArray(foreignKey?.columns);
  const referencesInput = foreignKey?.references || {};
  const referencedColumns = toStringArray(referencesInput?.columns);
  const datasetId =
    typeof referencesInput?.dataset_id === 'string'
      ? referencesInput.dataset_id.trim()
      : undefined;
  const table =
    typeof referencesInput?.table === 'string'
      ? referencesInput.table.trim()
      : undefined;

  const nextReference: DatasetCsvForeignKeyReference = {};
  if (datasetId) {
    nextReference.dataset_id = datasetId;
  }
  if (table) {
    nextReference.table = table;
  }
  if (referencedColumns.length) {
    nextReference.columns = referencedColumns;
  }

  if (!columns.length && !Object.keys(nextReference).length) {
    return null;
  }

  const normalized: DatasetCsvForeignKey = {};
  if (columns.length) {
    normalized.columns = columns;
  }
  if (Object.keys(nextReference).length) {
    normalized.references = nextReference;
  }

  return normalized;
};

export const normalizeDatasetCsvReference = (
  reference: DatasetCsvReference,
): DatasetCsvReference => {
  const next: DatasetCsvReference = {
    ...reference,
    s3_key: reference.s3_key,
  };

  const primaryKey = toStringArray(reference?.primary_key);
  if (primaryKey.length) {
    next.primary_key = primaryKey;
  } else {
    delete next.primary_key;
  }

  if (Array.isArray(reference?.foreign_keys)) {
    const normalizedForeignKeys = reference.foreign_keys
      .map((fk) => normalizeForeignKey(fk))
      .filter((fk): fk is DatasetCsvForeignKey => Boolean(fk));

    if (normalizedForeignKeys.length) {
      next.foreign_keys = normalizedForeignKeys;
    } else {
      delete next.foreign_keys;
    }
  } else {
    delete next.foreign_keys;
  }

  return next;
};
